
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tables } from "@/integrations/supabase/types";
import { ActivityFileUpload } from "./components/ActivityFileUpload";
import { BasicInformationSection } from "./components/BasicInformationSection";
import { SettingsSection } from "./components/SettingsSection";
import { ActivityFormActions } from "./components/ActivityFormActions";

type Activity = Tables<"activities">;
type ActivityCategory = Tables<"activity_categories">;
type ActivityInsert = {
  title: string;
  description?: string;
  type: "worksheet" | "game" | "craft" | "video" | "interactive";
  icon?: string;
  difficulty?: "easy" | "medium" | "hard";
  age_group?: string;
  download_url?: string;
  status?: "draft" | "published" | "archived";
  category_id?: string;
};

interface ActivityFormProps {
  activity?: Activity;
  categories: ActivityCategory[];
  onSave: (activity: ActivityInsert) => Promise<boolean>;
  onCancel: () => void;
}

export const ActivityForm = ({ activity, categories, onSave, onCancel }: ActivityFormProps) => {
  const [formData, setFormData] = useState<ActivityInsert>({
    title: activity?.title || "",
    description: activity?.description || "",
    type: activity?.type || "worksheet",
    icon: activity?.icon || "",
    difficulty: activity?.difficulty || "easy",
    age_group: activity?.age_group || "",
    download_url: activity?.download_url || "",
    status: activity?.status || "draft",
    category_id: activity?.category_id || "",
  });

  const [uploading, setUploading] = useState(false);

  const handleFieldChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handlePathChange = (path: string) => {
    setFormData({ ...formData, download_url: path });
  };

  const handleSubmit = async () => {
    const success = await onSave(formData);
    if (success) {
      onCancel();
    }
  };

  const isFormValid = formData.title.trim() !== "" && !uploading;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{activity ? "Edit Activity" : "Create New Activity"}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <BasicInformationSection
          formData={formData}
          categories={categories}
          onFieldChange={handleFieldChange}
        />

        <SettingsSection
          formData={formData}
          onFieldChange={handleFieldChange}
        />

        <ActivityFileUpload
          label="Activity File"
          currentPath={formData.download_url}
          onPathChange={handlePathChange}
          onUploadStateChange={setUploading}
        />

        <ActivityFormActions
          onSave={handleSubmit}
          onCancel={onCancel}
          isValid={isFormValid}
          uploading={uploading}
        />
      </CardContent>
    </Card>
  );
};
